"use client";

import ConditionalTooltip from "@/components/ConditionalTooltip";
import { FlexRowAlignCenter } from "@/components/Flex";
import Fade from "@mui/material/Fade";
import { type JSX, useCallback, useState } from "react";
import { isMacOs } from "react-device-detect";
import { EditorTooltipProps } from "./types";
import { themeColors } from "@/utils/themeColors";

const ShortcutKey = ({ children }: { children: string }): JSX.Element => {
    const style = {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "2rem",
        height: "2rem",
        padding: "0.4rem",
        fontSize: "1rem",
        borderRadius: "0.4rem",
        fontWeight: 600,
        border: `1px solid ${themeColors.neutral1}`,
        borderBottomWidth: "0.2rem",
        color: themeColors.neutralBlack,
    };

    if (children === "Mod") {
        return <kbd style={style}>{isMacOs ? "⌘" : "Ctrl"}</kbd>; // ⌃
    }

    if (children === "Shift") {
        return <kbd style={style}>⇧</kbd>;
    }

    if (children === "Alt") {
        return <kbd style={style}>{isMacOs ? "⌥" : "Alt"}</kbd>;
    }

    return <kbd style={style}>{children}</kbd>;
};

export const Tooltip = ({
    children,
    enabled = true,
    title,
    shortcut,
    TooltipProps,
}: EditorTooltipProps): JSX.Element => {
    const [isOpen, setIsOpen] = useState(false);

    const renderTooltip = useCallback(
        () => (
            <FlexRowAlignCenter
                as="span"
                style={{
                    gap: "0.8rem",
                    padding: "0.4rem 1rem",
                    color: themeColors.neutralBlack,
                    backgroundColor: themeColors.neutralWhite,
                    border: `1px solid ${themeColors.neutral1}`,
                    borderRadius: "0.5rem",
                    marginBottom: "-1rem",
                    zIndex: 999,
                }}
                tabIndex={-1}>
                {title && (
                    <span
                        style={{ fontSize: "1.2rem", color: themeColors.neutralBlack }}
                        data-testid="auto-id-d120b9ab-5bd7-4de1-969f-80f37b67fa2a">
                        {title}
                    </span>
                )}
                {shortcut && (
                    <FlexRowAlignCenter as="span">
                        {shortcut.map(shortcutKey => (
                            <ShortcutKey key={shortcutKey}>{shortcutKey}</ShortcutKey>
                        ))}
                    </FlexRowAlignCenter>
                )}
            </FlexRowAlignCenter>
        ),
        [shortcut, title]
    );

    return (
        <ConditionalTooltip
            tooltip={enabled ? renderTooltip() : null}
            arrow={false}
            enterDelay={500}
            open={isOpen}
            onOpen={() => setIsOpen(true)}
            onClick={() => setIsOpen(false)}
            onClose={() => setIsOpen(false)}
            slots={{ transition: Fade }}
            slotProps={{
                tooltip: { sx: { backgroundColor: "transparent" } },
                transition: { timeout: 100 },
            }}
            {...TooltipProps}>
            <span data-testid="auto-id-12dd2b77-1ddc-4aef-9e87-bba8e6bfb067">{children}</span>
        </ConditionalTooltip>
    );
};

export default Tooltip;
